import type { UserType } from "@/types/entities/users";
import { cn } from "@/utils/theme";
import {
  Avatar,
  AvatarFallback,
  Button,
  Heading,
  Icon,
} from "@elaniintc/goes-design-system";
import Pagination from "@/components/common/table/pagination";
import { useEffect, useMemo, useState } from "react";

interface Props {
  users: UserType[];
  onUnassign: (id: string) => void;
}

const PAGE_SIZE = 20;

const UsersList = ({ users, onUnassign }: Props) => {
  const [page, setPage] = useState(0);
  const localPagination = useMemo(() => {
    return {
      data: users.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
      meta: {
        total: users.length,
        page: 1,
        per_page: PAGE_SIZE,
      },
    };
  }, [page, users]);

  useEffect(() => {
    if (users.length > 0 && localPagination.data.length === 0 && page > 0) {
      setPage((p) => p - 1);
    }
  }, [
    users.length,
    page,
    localPagination.data.length,
    localPagination.meta.total,
  ]);

  if (users.length === 0) {
    return (
      <div className="h-screen w-full">
        <div className="w-full h-2 bg-neutral-object font-semibold text-xs border-y border-neutral relative">
          <div className="w-full h-full bg-neutral-object-accent-silent absolute inset-0" />
        </div>
        <div className="w-full h-full flex flex-col justify-center items-center">
          <Heading variant="h5" className="text-center text-heading-quiet">
            No se han añadido usuarios a este rol
          </Heading>
          <p className="leading-3xl font-normal text-body-quiet text-center">
            Añade usuarios a la lista para asignarles los permisos de este rol
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col flex-1 overflow-y-auto">
        <div className="w-full h-2 bg-neutral-object font-semibold text-xs border-y border-neutral relative">
          <div className="w-full h-full bg-neutral-object-accent-silent absolute inset-0" />
        </div>
        <ul className="overflow-y-auto min-h-[300px]">
          {localPagination.data.map(
            ({ id, keycloak_id, name, last_name }, index) => {
              const fallback = name ? name.charAt(0).toUpperCase() : "U"; // Default fallback if name is empty
              const stripper = index % 2 !== 0;
              return (
                <div
                  key={`user-${id}`}
                  className={cn(
                    stripper
                      ? "bg-neutral-object-accent-silent"
                      : "bg-neutral-object",
                  )}
                >
                  <div className="px-3 py-2.5 text-body font-normal text-xs flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Avatar size="xs">
                        <AvatarFallback color="action">
                          {fallback}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-body text-base leading-3xl">
                        {`${name} ${last_name}`}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      type="button"
                      aria-label="Eliminar usuario"
                      onClick={() => onUnassign(keycloak_id || "")}
                    >
                      <Icon type="far" name="times" />
                    </Button>
                  </div>
                </div>
              );
            },
          )}
        </ul>
      </div>
      <div className="w-full px-3 py-4">
        <Pagination
          currentPage={page + 1}
          totalPages={Math.ceil(users.length / PAGE_SIZE)}
          hasNext={page + 1 < Math.ceil(users.length / PAGE_SIZE)}
          hasPrevious={page > 0}
          onPageChange={(pages) => {
            setPage(pages - 1); // Adjusting for zero-based index
          }}
          onNext={() => {
            if (page + 1 < Math.ceil(users.length / PAGE_SIZE)) {
              setPage((prev) => prev + 1);
            }
          }}
          onPrevious={() => {
            if (page > 0) {
              setPage((prev) => prev - 1);
            }
          }}
          pageSize={localPagination.data.length}
          totalItems={localPagination.meta.total}
        />
      </div>
    </>
  );
};

export default UsersList;
